/**
 * Brand Studio — Version History panel.
 *
 * Lists every saved version of a project (generated / edited / restored
 * / imported), lets the admin restore an older one, edit text/palette
 * surgically (no LLM cost), and ingest output produced by an operator
 * working inside Emergent Lab.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Loader2, History, RotateCcw, Pencil, Download, Sparkles, FileText, X, Check,
  Trash2, FlaskConical, Copy, Upload, AlertCircle,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_STYLE = {
  generated: 'bg-[#00B4D8]/15 text-[#0369a1] border-[#00B4D8]/40',
  edited:    'bg-amber-100 text-amber-800 border-amber-300',
  restored:  'bg-purple-100 text-purple-800 border-purple-300',
  imported:  'bg-emerald-100 text-emerald-800 border-emerald-300',
};
const KIND_ICON = {
  generated: <Sparkles className="w-3 h-3" />,
  edited:    <Pencil className="w-3 h-3" />,
  restored:  <RotateCcw className="w-3 h-3" />,
  imported:  <FlaskConical className="w-3 h-3" />,
};

const VersionThumbnail = ({ version }) => {
  const assets = version?.ai_output?.assets || [];
  const palette = version?.ai_output?.palette || [];
  const logo = assets.find((a) => (a.kind || '').startsWith('logo'));
  const src = logo?.cdn_url || logo?.inline_base64 || null;
  return (
    <div className="flex items-center gap-3">
      <div className="w-16 h-16 rounded-lg border bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
        {src ? (
          <img src={src} alt="logo" className="max-w-full max-h-full object-contain" />
        ) : (
          <FileText className="w-5 h-5 text-gray-300" />
        )}
      </div>
      <div className="flex gap-0.5">
        {palette.slice(0, 5).map((c, i) => (
          <div
            key={i}
            className="w-3 h-12 rounded-sm border border-white/30"
            style={{ backgroundColor: c.hex || '#ccc' }}
            title={`${c.name || ''} ${c.hex || ''}`}
          />
        ))}
      </div>
    </div>
  );
};

const EditDialog = ({ version, onClose, onSaved, headers, projectId }) => {
  const { toast } = useToast();
  const ai = version?.ai_output || {};
  const [draft, setDraft] = useState({
    edit_notes: '',
    label: '',
    brand_voice: ai.brand_voice || '',
    tagline_candidates: (ai.tagline_candidates || []).join('\n'),
    palette: JSON.stringify(ai.palette || [], null, 2),
    typography_primary: ai.typography?.primary || '',
    typography_secondary: ai.typography?.secondary || '',
    typography_rationale: ai.typography?.rationale || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { edit_notes: draft.edit_notes || undefined, label: draft.label || undefined };
      if (draft.brand_voice !== (ai.brand_voice || '')) payload.brand_voice = draft.brand_voice;
      const newTags = draft.tagline_candidates.split('\n').map((s) => s.trim()).filter(Boolean);
      if (JSON.stringify(newTags) !== JSON.stringify(ai.tagline_candidates || [])) {
        payload.tagline_candidates = newTags;
      }
      try {
        const parsedPalette = JSON.parse(draft.palette);
        if (Array.isArray(parsedPalette) && JSON.stringify(parsedPalette) !== JSON.stringify(ai.palette || [])) {
          payload.palette = parsedPalette;
        }
      } catch {
        toast({ variant: 'destructive', title: 'Invalid palette JSON', description: 'Fix the palette array before saving.' });
        setSaving(false);
        return;
      }
      const typoChanged =
        draft.typography_primary !== (ai.typography?.primary || '') ||
        draft.typography_secondary !== (ai.typography?.secondary || '') ||
        draft.typography_rationale !== (ai.typography?.rationale || '');
      if (typoChanged) {
        payload.typography = {
          primary: draft.typography_primary,
          secondary: draft.typography_secondary,
          rationale: draft.typography_rationale,
        };
      }
      const r = await axios.patch(
        `${API_URL}/api/brand/projects/${projectId}/versions/${version.id}/edit`,
        payload,
        { headers }
      );
      toast({ title: `Saved as v${r.data.version_number}`, description: 'No LLM credits consumed.' });
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Save failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="version-edit-dialog">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="text-base font-bold text-[#0F172A] flex items-center gap-2">
            <Pencil className="w-4 h-4" /> Surgical edit — v{version.version_number}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 inline-flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" /> This will create a new EDITED version. No LLM call — instant.
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Version label (optional)</label>
            <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} className="mt-1 w-full px-3 py-1.5 border rounded text-sm" data-testid="edit-label" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Edit notes (audit trail)</label>
            <input value={draft.edit_notes} onChange={(e) => setDraft({ ...draft, edit_notes: e.target.value })} placeholder="e.g. Client asked to soften the tagline" className="mt-1 w-full px-3 py-1.5 border rounded text-sm" data-testid="edit-notes" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Brand voice</label>
            <textarea value={draft.brand_voice} onChange={(e) => setDraft({ ...draft, brand_voice: e.target.value })} rows={3} className="mt-1 w-full px-3 py-1.5 border rounded text-sm" data-testid="edit-brand-voice" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Tagline candidates (one per line)</label>
            <textarea value={draft.tagline_candidates} onChange={(e) => setDraft({ ...draft, tagline_candidates: e.target.value })} rows={5} className="mt-1 w-full px-3 py-1.5 border rounded text-sm font-mono" data-testid="edit-taglines" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-500">Display font</label>
              <input value={draft.typography_primary} onChange={(e) => setDraft({ ...draft, typography_primary: e.target.value })} className="mt-1 w-full px-3 py-1.5 border rounded text-sm" data-testid="edit-typo-primary" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-500">Body font</label>
              <input value={draft.typography_secondary} onChange={(e) => setDraft({ ...draft, typography_secondary: e.target.value })} className="mt-1 w-full px-3 py-1.5 border rounded text-sm" data-testid="edit-typo-secondary" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Typography rationale</label>
            <input value={draft.typography_rationale} onChange={(e) => setDraft({ ...draft, typography_rationale: e.target.value })} className="mt-1 w-full px-3 py-1.5 border rounded text-sm" data-testid="edit-typo-rationale" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Palette JSON (array of {`{name, hex, role}`})</label>
            <textarea value={draft.palette} onChange={(e) => setDraft({ ...draft, palette: e.target.value })} rows={8} className="mt-1 w-full px-3 py-1.5 border rounded text-sm font-mono" data-testid="edit-palette" />
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#0F172A] text-white" data-testid="edit-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
            Save as new version
          </Button>
        </div>
      </div>
    </div>
  );
};

const ImportDialog = ({ onClose, onImported, headers, projectId }) => {
  const { toast } = useToast();
  const [label, setLabel] = useState('Imported from Emergent Lab');
  const [notes, setNotes] = useState('');
  const [json, setJson] = useState('');
  const [busy, setBusy] = useState(false);

  const handleImport = async () => {
    if (!json.trim()) {
      toast({ variant: 'destructive', title: 'Missing JSON', description: 'Paste the Lab agent output.' });
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(json);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Invalid JSON', description: e.message });
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/brand/projects/${projectId}/versions/import-from-lab`,
        { label, notes, ai_output: parsed },
        { headers }
      );
      toast({ title: `Imported as v${r.data.version_number}`, description: 'Project moved to BMP Review.' });
      onImported(r.data);
      onClose();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Import failed', description: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="lab-import-dialog">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="text-base font-bold text-[#0F172A] flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-emerald-600" /> Import from Emergent Lab
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Version label</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1 w-full px-3 py-1.5 border rounded text-sm" data-testid="import-label" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Notes (optional)</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full px-3 py-1.5 border rounded text-sm" data-testid="import-notes" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Paste the Lab agent JSON output</label>
            <textarea value={json} onChange={(e) => setJson(e.target.value)} rows={14} className="mt-1 w-full px-3 py-1.5 border rounded text-sm font-mono" placeholder='{"tagline_candidates":[],"palette":[],...}' data-testid="import-json" />
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={handleImport} disabled={busy} className="bg-emerald-600 text-white" data-testid="import-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Import as new version
          </Button>
        </div>
      </div>
    </div>
  );
};

const BrandVersionsPanel = ({ project, headers, onChanged }) => {
  const { toast } = useToast();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [labPromptCopying, setLabPromptCopying] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/brand/projects/${project.id}/versions`, { headers });
      setVersions(r.data || []);
    } catch (e) {
      // silent: panel renders empty state
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [project.id]);

  const handleRestore = async (version) => {
    if (!window.confirm(`Restore v${version.version_number} as the current version?\n\nThis adds a new RESTORED version on top — nothing is deleted.`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/brand/projects/${project.id}/versions/${version.id}/restore`, {}, { headers });
      toast({ title: `Restored — now v${r.data.version_number}` });
      await reload();
      onChanged && (await onChanged());
    } catch (e) {
      toast({ variant: 'destructive', title: 'Restore failed', description: e.response?.data?.detail || e.message });
    }
  };

  const handleArchive = async (version) => {
    if (version.is_current) {
      toast({ variant: 'destructive', title: 'Cannot archive current version', description: 'Restore another version first.' });
      return;
    }
    if (!window.confirm(`Archive v${version.version_number}? It will be hidden from this list.`)) return;
    try {
      await axios.post(`${API_URL}/api/brand/projects/${project.id}/versions/${version.id}/archive`, {}, { headers });
      toast({ title: 'Version archived' });
      await reload();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Archive failed', description: e.response?.data?.detail || e.message });
    }
  };

  const handleCopyLabPrompt = async () => {
    setLabPromptCopying(true);
    try {
      const r = await axios.get(`${API_URL}/api/brand/projects/${project.id}/lab-prompt`, {
        headers, responseType: 'text', transformResponse: (x) => x,
      });
      await navigator.clipboard.writeText(r.data);
      toast({ title: 'Lab prompt copied', description: `${r.data.length} chars — paste it into the Emergent Lab agent.` });
    } catch (e) {
      toast({ variant: 'destructive', title: 'Copy failed', description: e.response?.data?.detail || e.message });
    } finally {
      setLabPromptCopying(false);
    }
  };

  const handleDownloadLabPrompt = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/brand/projects/${project.id}/lab-prompt`, {
        headers, responseType: 'text', transformResponse: (x) => x,
      });
      const blob = new Blob([r.data], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.project_code || 'BRD'}-lab-prompt.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Download failed', description: e.message });
    }
  };

  return (
    <>
      <Card data-testid="brand-versions-card">
        <CardContent className="pt-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-[#0F172A]" />
              <h3 className="text-base font-bold text-[#0F172A]">Version history</h3>
              <Badge variant="outline" className="text-[10px]">
                {versions.length} version{versions.length === 1 ? '' : 's'}
              </Badge>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline" size="sm"
                onClick={handleCopyLabPrompt} disabled={labPromptCopying}
                className="border-[#0F172A]/30 text-[#0F172A]"
                data-testid="copy-lab-prompt-btn"
              >
                {labPromptCopying ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                Copy Lab Prompt
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={handleDownloadLabPrompt}
                className="border-[#0F172A]/30 text-[#0F172A]"
                data-testid="download-lab-prompt-btn"
              >
                <Download className="w-3.5 h-3.5 mr-1" /> .txt
              </Button>
              <Button
                size="sm"
                onClick={() => setImportOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="open-import-lab-btn"
              >
                <FlaskConical className="w-3.5 h-3.5 mr-1" /> Import from Lab
              </Button>
            </div>
          </div>

          <p className="text-xs text-gray-500 mb-4">
            Every generation, edit, restore and Lab import is preserved. Surgical edits never consume LLM credits.
          </p>

          {loading ? (
            <div className="text-sm text-gray-500 py-6 text-center flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading versions…
            </div>
          ) : versions.length === 0 ? (
            <div className="text-sm text-gray-500 py-8 text-center bg-gray-50 rounded border border-dashed">
              No versions yet. Trigger AI generation or import a Lab output to begin.
            </div>
          ) : (
            <div className="space-y-2">
              {versions.map((v) => (
                <div
                  key={v.id}
                  className={`flex items-center justify-between gap-3 border rounded-lg p-3 ${
                    v.is_current ? 'border-[#00B4D8] bg-[#00B4D8]/5' : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                  data-testid={`version-row-${v.version_number}`}
                >
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <VersionThumbnail version={v} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-semibold text-[#0F172A]">
                          v{v.version_number}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-semibold ${KIND_STYLE[v.kind] || 'bg-gray-100 border-gray-200 text-gray-700'}`}>
                          {KIND_ICON[v.kind]} {v.kind}
                        </span>
                        {v.is_current && (
                          <Badge className="bg-[#00B4D8] text-white text-[10px]">CURRENT</Badge>
                        )}
                        {v.label && (
                          <span className="text-xs text-gray-600 truncate">— {v.label}</span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500 mt-0.5 truncate">
                        {new Date(v.created_at).toLocaleString()} · {v.created_by || 'system'}
                        {v.edited_fields && v.edited_fields.length > 0 && (
                          <span className="ml-2 text-amber-700">· edited: {v.edited_fields.join(', ')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!v.is_current && (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleRestore(v)}
                        className="text-xs"
                        data-testid={`restore-v${v.version_number}-btn`}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" /> Restore
                      </Button>
                    )}
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setEditTarget(v)}
                      className="text-xs"
                      data-testid={`edit-v${v.version_number}-btn`}
                    >
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    {!v.is_current && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => handleArchive(v)}
                        className="text-xs text-red-500 hover:bg-red-50"
                        data-testid={`archive-v${v.version_number}-btn`}
                        title="Archive"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {editTarget && (
        <EditDialog
          version={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { reload(); onChanged && onChanged(); }}
          headers={headers}
          projectId={project.id}
        />
      )}
      {importOpen && (
        <ImportDialog
          onClose={() => setImportOpen(false)}
          onImported={() => { reload(); onChanged && onChanged(); }}
          headers={headers}
          projectId={project.id}
        />
      )}
    </>
  );
};

export default BrandVersionsPanel;
